"use client";

import { useEffect, useRef } from "react";

type Props = {
  videoId: string;
  title: string;
  className?: string;
  loop?: boolean;
  threshold?: number;
};

export default function AutoplayYouTube({
  videoId,
  title,
  className,
  loop = false,
  threshold = 0.5,
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const send = (func: "playVideo" | "pauseVideo") => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*",
      );
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          send("playVideo");
        } else {
          send("pauseVideo");
        }
      },
      { threshold: [0, threshold, 1] },
    );

    observer.observe(iframe);
    return () => observer.disconnect();
  }, [threshold]);

  const params = new URLSearchParams({
    enablejsapi: "1",
    mute: "1",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    controls: "1",
  });
  if (loop) {
    params.set("loop", "1");
    params.set("playlist", videoId);
  }

  return (
    <iframe
      ref={iframeRef}
      className={className}
      src={`https://www.youtube.com/embed/${videoId}?${params.toString()}`}
      title={title}
      style={{ border: 0 }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
}
